import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Alert,
	Box,
	Chip,
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { CachedBadge } from 'features/salesDashboard/components/CachedBadge';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { CashTotalsOverview, cashBoxInfoTooltip, formatCashAmount } from '../BranchCashSummary';
import { useBranchReceiptsList } from '../../hooks/useBranchReceiptsList';
import routes from 'constants/routes';

interface BranchCashDetailProps {
	branch: string;
}

function formatDateTime(isoString: string): string {
	return new Date(isoString).toLocaleString('es', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
}

const depositStatusTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			Estado en el acta 📋
		</Typography>
		<Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
			<li>
				<strong>Sin estado</strong> → libre, podés incluirlo en un depósito nuevo.
			</li>
			<li>
				⏳ <strong>En borrador</strong> → ya está en un acta sin confirmar. No podés meterlo en otra.
			</li>
			<li>
				✅ <strong>Depositado</strong> → el acta fue confirmada, esperando que administración concilie.
			</li>
		</Typography>
	</>
);

/**
 * Detalle de una caja: totales + listado paginado de recibos sin depositar.
 * Muestra el estado en el acta (depositStatus) de cada recibo.
 */
export function BranchCashDetail({ branch }: BranchCashDetailProps): JSX.Element {
	const { receipts, pagination, totals, loading, loadingMore, error, cachedAt, loadMore, hasMore } =
		useBranchReceiptsList(branch, 25);

	return (
		<>
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							Recibos por depositar
						</Typography>
						<SectionInfoTooltip content={cashBoxInfoTooltip} />
					</Box>
					<CachedBadge cachedAt={cachedAt} />
				</Box>
				{loading ? (
					<Skeleton variant="rectangular" height={72} />
				) : error ? (
					<Alert severity="error">{error}</Alert>
				) : (
					<CashTotalsOverview
						totalReceipts={pagination?.total ?? 0}
						totalAmount={totals?.amountInMainCurrency ?? 0}
						byCurrency={totals?.byTransactionCurrency ?? []}
					/>
				)}
			</Paper>

			{loading ? (
				<Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
			) : !error && receipts.length > 0 ? (
				<>
					<TableContainer component={Paper} variant="outlined" sx={{ mb: 1, maxHeight: 560, overflow: 'auto' }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Recibo</TableCell>
									<TableCell>Fecha</TableCell>
									<TableCell align="right">Monto</TableCell>
									<TableCell align="right">Equiv. USD</TableCell>
									<TableCell>Orden</TableCell>
									<TableCell>Emitido</TableCell>
									<TableCell>
										<Box display="inline-flex" alignItems="center" gap={0.5}>
											Estado acta
											<SectionInfoTooltip content={depositStatusTooltip} />
										</Box>
									</TableCell>
									<TableCell>Observaciones</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{receipts.map((item) => (
									<TableRow key={item.id} hover>
										<TableCell sx={{ whiteSpace: 'nowrap' }}>
											<Box display="inline-flex" alignItems="center" gap={0.5}>
												<Typography
													variant="body2"
													fontWeight={500}
													color={Number(item.transactionAmount) < 0 ? 'error.main' : 'text.primary'}
												>
													{item.receiptNumber}
												</Typography>
												{item.pdfUrl && (
													<Tooltip title="Ver PDF del recibo">
														<a
															href={item.pdfUrl}
															target="_blank"
															rel="noopener noreferrer"
															style={{ display: 'inline-flex', alignItems: 'center', color: 'inherit' }}
														>
															<PictureAsPdfIcon sx={{ fontSize: 15, color: 'error.main', opacity: 0.75 }} />
														</a>
													</Tooltip>
												)}
											</Box>
										</TableCell>
										<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.createdAt)}</TableCell>
										<TableCell align="right">
											<Typography
												variant="body2"
												fontWeight={600}
												color={Number(item.transactionAmount) < 0 ? 'error.main' : 'text.primary'}
											>
												{formatCashAmount(Number(item.transactionAmount), item.transactionCurrency)}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography
												variant="body2"
												color={Number(item.amountInMainCurrency) < 0 ? 'error.main' : 'text.primary'}
											>
												{formatCashAmount(Number(item.amountInMainCurrency), 'USD')}
											</Typography>
										</TableCell>
										<TableCell>
											{item.saleOrderId ? (
												<RouterLink
													to={`/salesorder/${item.saleOrderId}`}
													style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 2 }}
												>
													Ver orden
													<OpenInNewIcon sx={{ fontSize: 11, opacity: 0.5 }} />
												</RouterLink>
											) : (
												<Typography variant="body2" color="text.disabled">
													—
												</Typography>
											)}
										</TableCell>
										<TableCell>
											{item.issued ? (
												<Tooltip
													title={`${item.issuedBy ?? ''}${item.issuedAt ? ` · ${formatDateTime(item.issuedAt)}` : ''}`}
												>
													<Chip label="Sí" size="small" color="success" variant="outlined" sx={{ fontSize: 11 }} />
												</Tooltip>
											) : (
												<Chip label="No" size="small" color="default" variant="outlined" sx={{ fontSize: 11 }} />
											)}
										</TableCell>
										<TableCell>
											{item.depositStatus === 'EN_BORRADOR' ? (
												<Tooltip title={`Acta #${item.depositId} sin confirmar`}>
													<Chip
														label="En borrador"
														size="small"
														variant="outlined"
														sx={{ fontSize: 11, cursor: 'pointer' }}
														component={RouterLink}
														to={routes.branchCash.depositDetail(item.depositId ?? 0)}
													/>
												</Tooltip>
											) : item.depositStatus === 'DEPOSITADO' ? (
												<Tooltip title="Acta confirmada — esperando conciliación de administración">
													<Chip
														label="Depositado"
														size="small"
														color="primary"
														variant="outlined"
														sx={{ fontSize: 11, cursor: 'pointer' }}
														component={RouterLink}
														to={routes.branchCash.depositDetail(item.depositId ?? 0)}
													/>
												</Tooltip>
											) : (
												<Typography variant="body2" color="text.disabled">
													—
												</Typography>
											)}
										</TableCell>
										<TableCell sx={{ maxWidth: 220 }}>
											<Typography variant="body2" color="text.secondary" noWrap title={item.observations ?? ''}>
												{item.observations ?? '—'}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					{hasMore && (
						<Box display="flex" justifyContent="center" mb={3}>
							<Chip
								label={loadingMore ? 'Cargando…' : `Cargar más (${receipts.length} de ${pagination?.total ?? 0})`}
								onClick={loadMore}
								variant="outlined"
								size="small"
								sx={{ cursor: 'pointer' }}
							/>
						</Box>
					)}
				</>
			) : null}
		</>
	);
}
